import React, { useState, useMemo } from 'react';
import { useApp } from '../contexts/AppContext';
import { DollarSign, Calculator } from 'lucide-react';

const formatCurrency = (val) => `$${(val || 0).toFixed(2)}`;

export default function LaborCalculator() {
  const { laborSettings, setLaborSettings } = useApp();
  const [form, setForm] = useState({
    hourlyWage: laborSettings.defaultWage || 15,
    laborBurdenPercent: laborSettings.laborBurdenPercent || 25,
    prepMinutes: 10,
    cookMinutes: 15,
  });

  const calculations = useMemo(() => {
    const wage = Number(form.hourlyWage) || 0;
    const burden = Number(form.laborBurdenPercent) || 0;
    const prep = Number(form.prepMinutes) || 0;
    const cook = Number(form.cookMinutes) || 0;
    const totalMinutes = prep + cook;
    const loadedRate = wage * (1 + burden / 100);
    const laborCostPerItem = loadedRate * totalMinutes / 60;
    const costPerMinute = loadedRate / 60;

    return { totalMinutes, loadedRate, laborCostPerItem, costPerMinute };
  }, [form]);

  const handleSaveDefaults = () => {
    setLaborSettings({
      defaultWage: Number(form.hourlyWage) || 15,
      laborBurdenPercent: Number(form.laborBurdenPercent) || 25,
    });
  };

  return (
    <div className="page-enter p-4 md:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-[#2E7D32] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]">
          <DollarSign className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-4xl sm:text-5xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="labor-title">
            Labor Cost Calculator
          </h1>
          <p className="text-sm text-[#2A2A2A]/60">Calculate labor cost per menu item.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Input Panel */}
        <div className="pizza-card p-6" data-testid="labor-form">
          <h2 className="text-2xl text-[#2A2A2A] mb-4" style={{ fontFamily: "'Bebas Neue', cursive" }}>
            Labor Inputs
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Hourly Wage ($)</label>
              <input type="number" step="0.01" min="0" value={form.hourlyWage}
                onChange={e => setForm(f => ({ ...f, hourlyWage: e.target.value }))}
                className="pizza-input w-full" data-testid="labor-wage-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Labor Burden % (taxes, insurance, benefits)</label>
              <input type="number" step="1" min="0" max="100" value={form.laborBurdenPercent}
                onChange={e => setForm(f => ({ ...f, laborBurdenPercent: e.target.value }))}
                className="pizza-input w-full" data-testid="labor-burden-input" />
              <p className="text-xs text-[#2A2A2A]/40 mt-1">Typical range: 20-30%</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Prep Minutes</label>
              <input type="number" step="1" min="0" value={form.prepMinutes}
                onChange={e => setForm(f => ({ ...f, prepMinutes: e.target.value }))}
                className="pizza-input w-full" data-testid="labor-prep-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Cook/Handling Minutes</label>
              <input type="number" step="1" min="0" value={form.cookMinutes}
                onChange={e => setForm(f => ({ ...f, cookMinutes: e.target.value }))}
                className="pizza-input w-full" data-testid="labor-cook-input" />
            </div>
            <button onClick={handleSaveDefaults} className="pizza-btn-secondary text-sm w-full" data-testid="labor-save-defaults-btn">
              Save as Default Settings
            </button>
          </div>
        </div>

        {/* Results Panel */}
        <div className="space-y-4">
          <div className="bg-[#2A2A2A] rounded-xl p-6 text-white" data-testid="labor-results">
            <h2 className="text-2xl mb-4" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Results
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-white/10 pb-3">
                <span className="text-white/60 text-sm">Total Minutes</span>
                <span className="text-xl font-bold" style={{ fontFamily: "'Bebas Neue', cursive" }}>{calculations.totalMinutes} min</span>
              </div>
              <div className="flex justify-between items-center border-b border-white/10 pb-3">
                <span className="text-white/60 text-sm">Loaded Hourly Rate</span>
                <span className="text-xl font-bold" style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatCurrency(calculations.loadedRate)}/hr</span>
              </div>
              <div className="flex justify-between items-center border-b border-white/10 pb-3">
                <span className="text-white/60 text-sm">Cost Per Minute</span>
                <span className="text-xl font-bold" style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatCurrency(calculations.costPerMinute)}/min</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-white/80 font-medium">Labor Cost Per Item</span>
                <span className="text-3xl font-bold text-[#FFC107]" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                  {formatCurrency(calculations.laborCostPerItem)}
                </span>
              </div>
            </div>
          </div>

          {/* Formula breakdown */}
          <div className="pizza-card p-6" data-testid="labor-formula">
            <h3 className="text-xl text-[#2A2A2A] mb-3" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              How It Works
            </h3>
            <div className="space-y-2 text-sm text-[#2A2A2A]/70">
              <p><strong>Loaded Rate</strong> = ${form.hourlyWage} x (1 + {form.laborBurdenPercent}%) = <strong>{formatCurrency(calculations.loadedRate)}/hr</strong></p>
              <p><strong>Total Time</strong> = {form.prepMinutes} prep + {form.cookMinutes} cook = <strong>{calculations.totalMinutes} min</strong></p>
              <p><strong>Labor Cost</strong> = {formatCurrency(calculations.loadedRate)} x {calculations.totalMinutes}/60 = <strong className="text-[#E23D28]">{formatCurrency(calculations.laborCostPerItem)}</strong></p>
            </div>
          </div>

          {/* Quick reference */}
          <div className="pizza-card p-6">
            <h3 className="text-xl text-[#2A2A2A] mb-3" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Quick Reference
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-[#2A2A2A]">
                    <th className="text-left py-2 text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>Minutes</th>
                    <th className="text-right py-2 text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {[5, 10, 15, 20, 25, 30, 45, 60].map(min => (
                    <tr key={min} className="border-b border-[#F0E6D2]">
                      <td className="py-2">{min} min</td>
                      <td className="text-right font-medium">{formatCurrency(calculations.loadedRate * min / 60)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
